import { AsyncForm, AsyncFormContentParams } from "src/components/common/forms/AsyncForm.tsx";
import {
	SalesPartEditApi,
	SalesPartEditApi_InitData,
} from "src/api/generated/erp/sales/masterData/api/salesPartEditApi.ts";
import { FormCommonProps } from "src/components/common/forms/types.ts";
import i18n from "i18next";
import {
	getObjectAccountingCodeIdsFromFormValues,
	getObjectAccountingFormDefaultValues,
} from "src/components/views/erp/common/accounting/ObjectAccountingForm.utils.ts";
import { useGlobalInitData } from "src/contexts/GlobalInitDataContext.ts";
import { DeepPartial } from "react-hook-form";
import { useGenericDialog } from "src/components/common/dialogs/GenericDialogContext.ts";
import { SalesPartContentLinesDataGrid } from "src/components/views/erp/sales/masterData/salesParts/SalesPartContentLinesDataGrid.tsx";
import {
	SalesPartFormContent,
	SalesPartFormValues,
} from "src/components/views/erp/sales/masterData/salesParts/SalesPartFormContent.tsx";
import { getSalesPartFormDefaultValues } from "src/components/views/erp/sales/masterData/salesParts/SalesPartForm.utils.ts";
import { SalesPart } from "src/api/generated/erp/db/types/tables/salesPart.ts";
import {
	ConfigurationDefaultPricingMethod,
} from "src/api/generated/erp/db/types/enums/configurationDefaultPricingMethod.ts";
import { useStoredState } from "src/utils/useStoredState.ts";
import { defineAgentUiContextKey } from "src/components/views/ai/assistant/uiContext/agentUiContextKey.ts";

export interface SalesPartFormProps extends FormCommonProps<number> {
	salesPartId: number | null;
	partId: number | null;
}

export const SalesPartForm = (props: SalesPartFormProps) => {
	const { openDialog } = useGenericDialog();
	const { salesPartId, partId, onCompleted, onFormEdited } = props;
	const { defaultSiteId } = useGlobalInitData();

	const [storedPricingMethod, setStoredPricingMethod] = useStoredState<ConfigurationDefaultPricingMethod>({
		key: "A3F8C2D1E5B69047",
		defaultValue: "BOM",
		type: "string",
	});

	return (
		<AsyncForm<SalesPartEditApi_InitData, SalesPartFormValues, number>
			columns={3}
			fetch={() =>
				SalesPartEditApi.getInitData({
					salesPartId: salesPartId,
					partId: partId,
				})
			}
			getDefaultValues={getDefaultValues}
			onCompleted={onCompleted}
			onFormEdited={onFormEdited}
			agentContext={{
				key: defineAgentUiContextKey("erp.sales.masterData.salesPart.form"),
				title: i18n.t("sales_part"),
				data: {
					salesPartId: salesPartId ?? null,
					partId: partId ?? null,
				},
			}}
			render={({ data, ...formContentParams }) => (
				<SalesPartFormContent
					salesPartId={salesPartId}
					part={data.part}
					initData={data}
					{...formContentParams}
				/>
			)}
			submit={submit}
		/>
	);

	function getDefaultValues(initData: SalesPartEditApi_InitData): DeepPartial<SalesPartFormValues> {
		const { salesPart, accountingDimensions, importCodes } = initData;
		if (salesPart != null)
			return {
				...salesPart,
				...getObjectAccountingFormDefaultValues(accountingDimensions),
				importCodes,
			};
		else {
			return getSalesPartFormDefaultValues({
				part: initData.part,
				defaultSiteId,
				vatCodeOptions: initData.vatCodeOptions,
				accountingDimensions: initData.accountingDimensions.map((dimensionValue) => dimensionValue.dimension),
				configurationDefaultPricingMethod: storedPricingMethod,
			});
		}
	}

	async function submit(
		values: SalesPartFormValues,
		{
			data: { part, salesPart: existingSalesPart },
		}: AsyncFormContentParams<SalesPartEditApi_InitData, SalesPartFormValues>,
	): Promise<number> {
		if (values.configurationDefaultPricingMethod != null)
			setStoredPricingMethod(values.configurationDefaultPricingMethod);

		const { accountingDimensionValues, importCodes, ...salesPart } = values;
		const accountingCodeIds = getObjectAccountingCodeIdsFromFormValues(accountingDimensionValues);

		if (existingSalesPart == null) {
			const createdSalesPartId = await SalesPartEditApi.insert({
				salesPart: salesPart as SalesPart,
				accountingCodeIds,
				importCodes,
			});

			if (values.salesPartType === "PACKAGE_" && part?.acquisitionMethod !== "SPLIT_BOM")
				openDialog({
					title: i18n.t("package_lines"),
					size: "lg",
					content: <SalesPartContentLinesDataGrid parentSalesPartId={createdSalesPartId} />,
					agentContext: {
						key: defineAgentUiContextKey("erp.sales.masterData.salesPartPackageContent.dialog"),
						title: i18n.t("package_lines"),
						pathSegments: [i18n.t("package_lines")],
						data: { parentSalesPartId: createdSalesPartId },
					},
				});

			return createdSalesPartId;
		} else {
			return await SalesPartEditApi.update({
				request: {
					...salesPart,
					salesPartId: existingSalesPart.salesPartId,
					lastUpdated: existingSalesPart.lastUpdated,
				},
				accountingCodeIds,
				importCodes,
			});
		}
	}
};
